import { Text, textVariants } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { useEffect, useState } from 'react';
import { countdownFactory } from '../../utils/useCountdown';
import { screenReadersOnlyStyles } from '../../utils/styleUtils';

interface CountdownTimerProps {
  code: string;
  onExpire: () => void;
  storageKey: string;
  variant?: (typeof textVariants)[keyof typeof textVariants];
}
const CountdownTimer = ({
  code,
  onExpire,
  storageKey,
  variant = textVariants._16,
}: CountdownTimerProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language, timeFormat } = useSiteContext();
  const lowerTextCode: string = code.toLowerCase();

  const configuredTimerDurationMinutes = Number(
    p(`Payment.${code}.Processing.TimerDurationMinutes`),
  );

  const DEFAULT_TIMER_DURATION_MINUTES = 5;

  const timerDurationMinutes =
    configuredTimerDurationMinutes > 0
      ? configuredTimerDurationMinutes
      : DEFAULT_TIMER_DURATION_MINUTES;

  const { useCountdown } = countdownFactory(
    language.code,
    timeFormat,
    storageKey,
    timerDurationMinutes,
  );

  const { formattedTimeLeft, isoTimeLeft, isExpired } = useCountdown();

  const [screenReaderTimeLeft, setScreenReaderTimeLeft] = useState({
    formattedTimeLeft,
    isoTimeLeft,
  });

  useEffect(() => {
    if (isExpired) {
      onExpire();
    }
  }, [isExpired, onExpire]);

  useEffect(() => {
    if (isoTimeLeft.endsWith('M0S') || isoTimeLeft.endsWith('M30S')) {
      setScreenReaderTimeLeft({
        formattedTimeLeft,
        isoTimeLeft,
      });
    }
  }, [formattedTimeLeft, isoTimeLeft]);

  return (
    <Text aria-labelledby="countdown-timer" as="p">
      <Text
        aria-hidden="true"
        as="span"
        asHtml
        data-testid={`processing-${lowerTextCode}-countdown-visually-shown`}
        variant={variant}
      >
        {t(
          `Processing.${code}.RemainingTime.Text`,
          `<strong><time datetime="${isoTimeLeft}">${formattedTimeLeft}</time></strong>`,
        )}
      </Text>

      <Text
        aria-atomic="true"
        aria-live="polite"
        as="span"
        asHtml
        className={screenReadersOnlyStyles}
        data-testid={`processing-${lowerTextCode}-countdown-visually-hidden`}
        id="countdown-timer"
        role="alert"
      >
        {t(
          `Processing.${code}.RemainingTime.Text`,
          `<time datetime="${screenReaderTimeLeft.isoTimeLeft}">${screenReaderTimeLeft.formattedTimeLeft}</time>`,
        )}
      </Text>
    </Text>
  );
};

export default CountdownTimer;
